import Link from "next/link";
import { createUseStyles } from "react-jss";
import classNames from "classnames";
import BodyClassName from "react-body-classname";
import Colors from "../utils/Colors";
import { storePageTypes } from "../utils/Constants";
import Nav, { NAV_HEIGHT } from "./Nav";
import Button from "./Button";
import HomeIcon from "./icons/nav/HomeIcon";
import OrdersIcon from "./icons/nav/OrdersIcon";
import SupportIcon from "./icons/nav/SupportIcon";
import ProfileIcon from "./icons/nav/ProfileIcon";

const CUSTOM_ICON_SIZE = 24;
const MAIN_CONTENT_WIDTH = 880;

const MEMBERSHIP_SHOPS = ["daikanyama-pink"];

const useCustomShopIconStyles = createUseStyles(() => ({
  container: {
    height: CUSTOM_ICON_SIZE,
    width: CUSTOM_ICON_SIZE,
    backgroundSize: CUSTOM_ICON_SIZE,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    display: "inline-block",
    filter:
      "invert(81%) sepia(1%) saturate(971%) hue-rotate(177deg) brightness(88%) contrast(92%)",
  },
  containerSelected: {
    filter:
      "invert(52%) sepia(46%) saturate(3930%) hue-rotate(322deg) brightness(103%) contrast(101%)",
  },
}));

const makeCustomIcon =
  (shop) =>
  ({ color = Colors.primaryGray }) => {
    const classes = useCustomShopIconStyles();

    return (
      <div
        style={{
          backgroundImage: `url(${shop.media.logo})`,
        }}
        className={classNames(classes.container, {
          [classes.containerSelected]: color === Colors.primaryPink,
        })}
      />
    );
  };

const makeNavItemsForShopsApp = (shop) => {
  const isMembershipShop = shop?.storePage?.type === storePageTypes.MEMBERSHIP;

  if (isMembershipShop) {
    return [
      {
        key: "home",
        label: "メニュー",
        icon: shop?.media?.logo ? makeCustomIcon(shop) : HomeIcon,
        path: `/s/${shop.descriptor}`,
      },
      {
        key: "support",
        label: "メッセージ",
        icon: SupportIcon,
        path: `/s/${shop.descriptor}/support`,
      },
      {
        key: "profile",
        label: "マイページ",
        icon: ProfileIcon,
        path: `/s/${shop.descriptor}/profile`,
      },
    ];
  }

  return [
    {
      key: "home",
      label: "メニュー",
      icon: shop?.media?.logo ? makeCustomIcon(shop) : HomeIcon,
      path: `/s/${shop.descriptor}`,
    },
    {
      key: "orders",
      label: "履歴",
      icon: OrdersIcon,
      path: `/s/${shop.descriptor}/orders`,
    },
    {
      key: "support",
      label: "メッセージ",
      icon: SupportIcon,
      path: `/s/${shop.descriptor}/support`,
    },
    {
      key: "profile",
      label: "マイページ",
      icon: ProfileIcon,
      path: `/s/${shop.descriptor}/profile`,
    },
  ];
};

const useStyles = createUseStyles((theme) => ({
  bodyGray: {
    backgroundColor: Colors.backgroundGray,
  },
  barContainer: {
    zIndex: 5,
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,

    backgroundColor: Colors.white,
  },
  bar: {
    height: NAV_HEIGHT,
    width: "100%",
    maxWidth: MAIN_CONTENT_WIDTH + theme.spacing.unit * 2,
    paddingLeft: theme.spacing.unit,
    paddingRight: theme.spacing.unit,
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  logo: {
    width: 170,
    height: NAV_HEIGHT,
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },
  logoMobile: {
    width: 58,
    height: NAV_HEIGHT,

    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },
  logoDesktop: {
    display: "none",
    width: 170,
    height: NAV_HEIGHT,
    cursor: "pointer",

    [theme.breakpoints.up("sm")]: {
      display: "block",
    },
  },
  nav: {
    width: "max-content",
  },
  navItem: {
    width: 60,
  },
  signupCtaContainer: {
    width: 120,
  },
  signupCta: {
    height: 36,
  },
  content: {
    paddingTop: NAV_HEIGHT,
  },
}));

const ShopsAppFrame = ({
  backgroundColor = "white",
  showNav = true,
  showSignupCta = false,
  selectedKey = "home",
  shop,
  onClickSignup,
  children,
}) => {
  const classes = useStyles();
  const navItems = makeNavItemsForShopsApp(shop);

  const onClickLogo = () => {};

  return (
    <div>
      {backgroundColor === "gray" && (
        <BodyClassName className={classes.bodyGray} />
      )}
      <div className={classes.barContainer}>
        <div className={classes.bar}>
          <Link href={`/s/${shop.descriptor}`}>
            <div
              className={classNames(classes.logo, classes.logoMobile)}
              style={{
                backgroundImage: `url(${shop.media?.logo || shop.media?.logoWide})`,
              }}
              onClick={onClickLogo}
            />
          </Link>
          <Link href={`/s/${shop.descriptor}`}>
            <div
              className={classNames(classes.logo, classes.logoDesktop)}
              style={{
                backgroundImage: `url(${shop.media?.logoWide})`,
              }}
              onClick={onClickLogo}
            />
          </Link>
          {showNav && (
            <Nav
              items={navItems}
              selectedKey={selectedKey}
              classes={{
                root: classes.nav,
                item: classes.navItem,
              }}
            />
          )}
          {showSignupCta && (
            <div className={classes.signupCtaContainer}>
              <Button
                text="サインイン"
                className={classes.signupCta}
                onClick={onClickSignup}
              />
            </div>
          )}
        </div>
      </div>
      <div className={classes.content}>{children}</div>
    </div>
  );
};

export default ShopsAppFrame;
